import { enGB as dateFnsLocaleEnGb } from 'date-fns/locale';
import { capitalize } from 'lodash';

import {
  DATE_FORMAT,
} from '@/helpers/date';

import * as bikeConstants from '@/constants/products/bike';
import * as commonConstants from '@/constants/common';

const constants = Object.assign(
  {},
  commonConstants,
  bikeConstants,
);

export const attrsForNumKeyboard = {
  inputmode: 'numeric',
  pattern: '[0-9]*',
  type: 'number',
};

export const licencePrefillNameRegex = /^[A-Za-z-\s]+$/;
export const regNumberRegex = /^[A-Za-z0-9\s]{0,10}$/;

export const createOptions = ({ source, valueKey = 'value', textKey = 'text', asNumbers }) => {
  if (Array.isArray(source)) {
    return source.map(item => ({
      [valueKey]: asNumbers ? +item : item,
      [textKey]: item,
    }));
  }
  return Object.keys(source).map(key => ({
    [valueKey]: asNumbers ? +key : key,
    [textKey]: source[key],
  }));
};

export const moveCursorToEnd = el => {
  if (typeof el.selectionStart === 'number') {
    el.selectionStart = el.selectionEnd = el.value.length;
  } else if (typeof el.createTextRange !== 'undefined') {
    el.focus();
    var range = el.createTextRange();
    range.collapse(false);
    range.select();
  }
};

export const monthsOptions = [...Array(12)].map((_, i) => ({
  value: i + 1,
  text: dateFnsLocaleEnGb.localize.month(i),
}));

export const limitValueChars = (value, maxChars = 3) => {
  return () => {
    if (!value) return value;
    return value.length > maxChars ? value.substring(0, maxChars) : value;
  };
};

export const limitInputChars = function (step, value, limit) {
  step[value] = (limitValueChars(step[value], limit))();
};

export const getPlaceholderResponsiveText = (text, isMobile) => {
  if (!isMobile) return text;
  if (text.indexOf('Please ') !== 0) return text;
  return capitalize(text.replace('Please ', ''));
};

export const deepRemoveKeys = (obj, keys = [], testKey = () => false) => {
  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      if ((Array.isArray(keys) && keys.includes(prop)) || testKey(prop)) {
        delete obj[prop];
      } else if (typeof (obj[prop]) === 'object') {
        deepRemoveKeys(obj[prop], keys, testKey);
      }
    }
  }
};

/**
 * Returns a string with a UK locale representation of this number
 * @param {Number} number
 * @param {String} locale='en-gb'
 * @returns {String} formatted string in the UK locale
 */
export const getNumberFormatted = (number, locale = 'en-gb') => {
  return number.toLocaleString(locale);
};

export const bikeStructure = [
  {
    stepId: 'vehicle',
    stepLabel: 'Vehicle Details',
    groups: [
      {
        groupId: 'confirm',
        groupLabel: 'Confirm your bike',
        sections: [
          {
            sectionId: 'select-vehicle',
            sectionLabel: 'Do you know the registration number of the motorbike?',
            fields: [
              'vehicleConfirm',
              'vehicleMake',
              'vehicleModel',
              'engineSize',
              'yearOfManufacture',
              'vehicleRegistrationNumber',
            ],
          },
        ],
      },
      {
        groupId: 'about',
        groupLabel: 'About your bike',
        sections: [
          {
            sectionId: 'vehicle-alarm',
            sectionLabel: 'Is the bike fitted with an alarm/immobiliser?',
            fields: ['vehicleAlarm'],
          },
          {
            sectionId: 'vehicle-tracking-device',
            sectionLabel: 'Does the bike have a tagging device/secure markings?',
            fields: ['vehicleTrackingDevice'],
          },
          {
            sectionId: 'vehicle-physical-security',
            sectionLabel: 'Does the bike have any physical security? E.g. brake disc locks.',
            fields: ['vehiclePhysicalSecurity'],
          },
          {
            sectionId: 'modifications',
            sectionLabel: 'Has the bike been modified in any way?',
            fields: [
              'vehicleModifiedFromManufacturer',
              'vehicleModifications',
            ],
          },
          {
            sectionId: 'vehicle-grey-or-import',
            sectionLabel: 'Is the bike imported?',
            fields: ['vehicleGreyOrImport'],
          },
          {
            sectionId: 'estimated-value',
            sectionLabel: 'Roughly, how much is the bike worth? (Excluding accessories)',
            fields: ['estimatedValue'],
          },
          {
            sectionId: 'estimated-accesories-value',
            sectionLabel: 'What is the estimated value of fitted accessories?',
            fields: ['estimatedAccesoriesValue'],
          },
          {
            sectionId: 'vehicle-purchase',
            sectionLabel: 'Have you bought the bike yet?',
            fields: [
              'vehiclePurchased',
              'vehicleBought',
            ],
          },
          {
            sectionId: 'vehicle-kept',
            sectionLabel: 'Where is this bike kept overnight?',
            fields: ['vehicleKept'],
          },
        ],
      },
    ],
  },
  {
    stepId: 'drivers',
    stepLabel: 'About You',
    groups: [
      {
        groupId: 'information',
        groupLabel: 'Your information',
        sections: [
          {
            sectionId: 'title',
            sectionLabel: 'What is your title?',
            sectionLabelAdditional: 'What is their title?',
            fields: ['title'],
          },
          {
            sectionId: 'name',
            sectionLabel: 'What is your name?',
            sectionLabelAdditional: 'What is their name?',
            fields: [
              'firstName',
              'surname',
            ],
          },
          {
            sectionId: 'date-of-birth',
            sectionLabel: 'What is your date of birth?',
            sectionLabelAdditional: 'What is their date of birth?',
            fields: ['dateOfBirth'],
          },
          {
            sectionId: 'uk-resident',
            sectionLabel: 'How long have you continuously lived in the UK?',
            sectionLabelAdditional: 'How long have they continuously lived in the UK?',
            fields: [
              'bornInUk',
              'dateOfUkResident',
            ],
            dependent: [
              {
                id: 'dateOfUkResident',
                controlledBy: 'bornInUk',
                inverted: true,
              },
            ],
          },
          {
            sectionId: 'marital-status',
            sectionLabel: 'What is your relationship status?',
            sectionLabelAdditional: 'What is their relationship status?',
            fields: ['maritalStatus'],
          },
          {
            sectionId: 'relationship-proposer',
            sectionLabel: 'What is their relationship to you?',
            fields: ['relationshipProposer'],
          },
        ],
      },
      {
        groupId: 'contacts',
        groupLabel: 'Your contact details',
        sections: [
          {
            sectionId: 'telephone',
            sectionLabel: 'Phone number',
            fields: ['telephone'],
            additional: false,
          },
          {
            sectionId: 'address',
            sectionLabel: 'What\'s your postcode',
            fields: [
              'postcode',
              'address',
              'houseNumberOrName',
              'addressLine2',
              'addressLine3',
              'addressLine4',
            ],
            additional: false,
          },
          {
            sectionId: 'kept-address',
            sectionLabel: 'Is the motorcycle kept at this address?',
            fields: [
              'keptAddress',
              'keptPostcode',
            ],
            additional: false,
          },
          {
            sectionId: 'home-owner',
            sectionLabel: 'Do you own your own home?',
            fields: ['homeOwner'],
            additional: false,
          },
          {
            sectionId: 'email',
            sectionLabel: 'Email address',
            fields: ['email'],
            additional: false,
          },
          {
            sectionId: 'use-of-other-vehicles',
            sectionLabel: 'Do you drive any other vehicle than the one to be insured?',
            sectionLabelAdditional: 'Does this rider drive any other vehicles, other than the one to be insured?',
            fields: ['useOfOtherVehicles'],
          },
        ],
      },
      {
        groupId: 'occupation',
        groupLabel: 'Your occupation',
        sections: [
          {
            sectionId: 'employment-status',
            sectionLabel: 'What is your employment status?',
            sectionLabelAdditional: 'What is their employment status?',
            fields: ['employmentStatus'],
          },
          {
            sectionId: 'occupation',
            sectionLabel: 'What do you do for a living?',
            sectionLabelAdditional: 'What do they do for a living?',
            fields: [
              'occupationList',
              'occupationKey',
            ],
          },
          {
            sectionId: 'business',
            sectionLabel: 'Which industry do you work in?',
            sectionLabelAdditional: 'Which industry do they work in?',
            fields: [
              'businessKey',
              'businessList',
            ],
          },
          {
            sectionId: 'another-job',
            sectionLabel: 'Do you have another job?',
            sectionLabelAdditional: 'Do they have another job?',
            fields: [
              'partTimeOccupation',
              'businessKeyPt',
              'businessListPt',
              'occupationListPt',
              'occupationKeyPt',
            ],
          },
        ],
      },
      {
        groupId: 'licence',
        groupLabel: 'Your licence',
        sections: [
          {
            sectionId: 'type-of-driving',
            sectionLabel: 'What kind of motorcycle driving licence do you have?',
            sectionLabelAdditional: 'What kind of motorcycle driving licence do they have?',
            fields: ['typeOfDriving'],
          },
          {
            sectionId: 'period-of-licence-held',
            sectionLabel: 'How many years have you held your licence?',
            sectionLabelAdditional: 'How many years have they held their licence?',
            fields: ['periodOfLicenceHeld'],
          },
          {
            sectionId: 'has-car-licence',
            sectionLabel: 'Do you hold a car licence?',
            fields: ['hasCarLicence'],
            additional: false,
          },
          {
            sectionId: 'car-type-of-driving',
            sectionLabel: 'What kind of car driving licence do you have?',
            fields: ['carTypeOfDriving'],
            additional: false,
          },
          {
            sectionId: 'car-period-of-licence-held',
            sectionLabel: 'How many years have you held this car licence?',
            fields: ['carPeriodOfLicenceHeld'],
            additional: false,
          },
          {
            sectionId: 'licence-number',
            sectionLabel: 'Would you like to provide your Driving Licence Number?',
            sectionLabelAdditional: 'Would you like to provide their Driving Licence Number?',
            fields: [
              'licenceNumberYesNo',
              'licenceNumber',
              'licence-number_1', // logical
              'licence-number_2', // logical
              'licence-number_3', // logical
              'licence-number_4', // logical
            ],
          },
          {
            sectionId: 'driver-cbt',
            sectionLabel: 'Have you passed your CBT?',
            sectionLabelAdditional: 'Have they passed their CBT?',
            fields: [
              'driverCbt',
              'cbtDate',
            ],
          },
          {
            sectionId: 'driver-experience',
            sectionLabel: 'How many years continuous riding experience do you have?',
            sectionLabelAdditional: 'How many years continuous riding' +
              ' experience do they have?',
            fields: ['driverExperience'],
          },
          {
            sectionId: 'riding-qualifications',
            sectionLabel: 'Do you have any riding qualifications?',
            sectionLabelAdditional: 'Do they have any riding qualifications?',
            fields: ['ridingQualifications'],
          },
          {
            sectionId: 'biking-organisation',
            sectionLabel: 'Are you a member of a biking organisation?',
            sectionLabelAdditional: 'Are they a members of a biking' +
              ' organisation?',
            fields: ['bikingOrganisation'],
          },
          {
            sectionId: 'medical-conditions',
            sectionLabel: 'Do you have any medical conditions or disabilities that the DVLA needs to be notified of?',
            sectionLabelAdditional: 'Do they have any medical conditions or' +
              ' disabilities that the DVLA needs to be notified of?',
            fields: ['medicalConditions'],
          },
        ],
      },
      {
        groupId: 'driving-history',
        groupLabel: 'Your driving history',
        sections: [
          {
            sectionId: 'claims',
            sectionLabel: 'Have you had any motor accidents, claims or losses in the past 5 years, no matter who was at fault or if a claim was made?',
            sectionLabelAdditional: 'Have they had any motor accidents, claims or losses in the past 5 years, no matter who was at fault or if a claim was made?',
            fields: [
              'hadAnyAccidents',
              'claims',
              'claims-new',
              {
                parent: 'claim',
                fields: [
                  'dateOfClaim',
                  'costOfRepairs',
                  'causeOfDescription',
                  'currentPolicy',
                ],
              },
              'claimNew',
            ],
          },
          {
            sectionId: 'convictions',
            sectionLabel: 'Have you had any driving related convictions, endorsements, penalties, disqualifications or bans in the past 5 years?',
            sectionLabelAdditional: 'Have they had any driving related convictions, endorsements, penalties, disqualifications or bans in the past 5 years?',
            fields: [
              'hadAnyConvictions',
              'convictions',
              'convictions-new',
              {
                parent: 'conviction',
                fields: [
                  'dateOfConviction',
                  'convictionDeclaredAccident',
                  'offenceCode',
                  'alcoholMethodTest',
                  'alcoholLevel',
                  'pointsReceived',
                  'howMuchWereFined',
                  'receivedBanPeriod',
                ],
              },
              'convictionNew',
            ],
          },
          {
            sectionId: 'had-any-non-mot-convictions',
            sectionLabel: 'Have you got any unspent non-motoring-related criminal convictions?',
            sectionLabelAdditional: 'Have they got any unspent non-motoring-related criminal convictions?',
            fields: ['hadAnyNonMotConvictions'],
          },
        ],
      },
    ],
  },
  {
    stepId: 'cover',
    stepLabel: 'Cover',
    groups: [
      {
        groupId: 'cover-details',
        groupLabel: 'Cover details',
        sections: [
          {
            sectionId: 'additional-drivers',
            sectionLabel: 'Would you like to add any additional riders to this policy?',
            fields: [
              'additional-drivers-type-of-use',
              'additional-drivers-relationship-to-proposer',
              'has-additional-drivers',
              'additional-drivers-validity',
            ],
          },
          {
            sectionId: 'date-of-insurance-start',
            sectionLabel: 'When would you like your insurance to start?',
            fields: ['dateOfInsuranceStart'],
          },
          {
            sectionId: 'type-of-cover',
            sectionLabel: 'What type of cover would you like?',
            fields: ['typeOfCover'],
          },
          {
            sectionId: 'type-of-use',
            sectionLabel: 'What will you use the bike for?',
            fields: ['typeOfUse'],
          },
          {
            sectionId: 'annual-mileage',
            sectionLabel: 'What is your annual mileage?',
            fields: ['annualMileage'],
          },
          {
            sectionId: 'business-mileage',
            sectionLabel: 'If you have requested business use, what is your annual business mileage?',
            fields: ['businessMileage'],
          },
          {
            sectionId: 'voluntary-excess',
            sectionLabel: 'What voluntary excess would you like on this policy?',
            fields: ['voluntaryExcess'],
          },
        ],
      },
      {
        groupId: 'no-claims-discount',
        groupLabel: 'No Claims Discount',
        sections: [
          {
            sectionId: 'no-claims-bonus',
            sectionLabel: 'How many years no claims discount (NCD) do you currently have for this bike?',
            fields: ['noClaimsBonus'],
          },
          {
            sectionId: 'protect-bonus',
            sectionLabel: 'Do you want to protect your no claims bonus?',
            fields: ['protectBonus'],
          },
        ],
      },
      {
        groupId: 'final-details',
        groupLabel: 'Final Details',
        sections: [
          {
            sectionId: 'pillion-passenger',
            sectionLabel: 'Will you ever carry a pillion passenger?',
            fields: ['pillionPassenger'],
          },
          {
            sectionId: 'main-user-of-vehicle',
            sectionLabel: 'Who is the main driver of this motorcycle?',
            fields: ['mainUserOfVehicle'],
          },
          {
            sectionId: 'owner-registered',
            sectionLabel: 'Are you (or will you be) the owner and registered keeper of the bike?',
            fields: [
              'vehicle-owner-and-registered', // logical
              'ownerOfVehicle',
              'registeredOfVehicle',
            ],
          },
          {
            sectionId: 'pay-insurance-monthly',
            sectionLabel: 'Do you normally pay for your insurance monthly?',
            fields: ['payInsuranceMonthly'],
          },
          {
            sectionId: 'insurance-ever-declined',
            sectionLabel: 'Have you or any rider ever had insurance declined, cancelled, voided or special terms imposed?',
            fields: ['insuranceEverDeclined'],
          },
          {
            sectionId: 'user-data',
            sectionLabel: 'Account details',
            fields: ['userData', 'userDataConfirm', 'userLoginEmail', 'userLoginPassword', 'userLoginOneTimePassword'],
          },
          {
            sectionId: 'outbound',
            sectionLabel: 'Finding the right cover',
            fields: ['outbound'],
          },
          {
            sectionId: 'direct-marketing-options',
            sectionLabel: 'Marketing options',
            fields: [
              'directMarketingEmail',
              'directMarketingSms',
              'directMarketingPost',
              'directMarketingPhone',
            ],
          },
          {
            sectionId: 'user-keep-sign-in',
            sectionLabel: 'Keep me signed in',
            fields: ['userKeepSignIn'],
          },
        ],
      },
    ],
  },
];

export const getClaimsRules = claim => ({
  dateOfClaim: {
    date_format: DATE_FORMAT,
    before: {
      compare: 'today',
      equal: true,
    },
    after: {
      compare: '-5 years',
    },
  },
  costOfRepairs: {
    integer: true,
    between: [0, 9999999],
  },
  currentPolicy: {
    required: true,
    typeof: 'boolean',
  },
  causeOfDescription: {
    is_not: { other: null },
    in: {
      items: constants.DRIVER_CLAIM_CAUSE_OF_DESCRIPTION,
    },
  },
});

export const getConvictionsRules = conviction => ({
  dateOfConviction: {
    date_format: DATE_FORMAT,
    before: {
      compare: 'today',
      equal: true,
    },
    after: {
      compare: '-5 years',
    },
  },
  convictionDeclaredAccident: {
    required: { allowFalse: true },
    typeof: 'boolean',
  },
  offenceCode: {
    is_not: { other: null },
    typeof: 'string',
  },
  alcoholLevel: {
    integer: true,
    between: [0, 999],
    switchedBy: {
      condition: constants.DRIVER_CONVICTION_ALCOHOL_LEVEL_REQUIRED_CODES.includes(conviction.offenceCode),
    },
  },
  alcoholMethodTest: {
    in: {
      items: constants.DRIVER_CONVICTION_ALCOHOL_METHOD_TEST,
      required: constants.DRIVER_CONVICTION_ALCOHOL_LEVEL_REQUIRED_CODES.includes(conviction.offenceCode),
    },
    switchedBy: {
      condition: constants.DRIVER_CONVICTION_ALCOHOL_LEVEL_REQUIRED_CODES.includes(conviction.offenceCode),
    },
  },
  howMuchYouFined: {
    is_not: { other: null },
    integer: true,
    between: [0, 99999],
  },
  pointsReceived: {
    is_not: { other: null },
    integer: true,
    in: {
      items: constants.DRIVER_CONVICTION_POINTS_RECEIVED,
    },
  },
  receivedBanPeriod: {
    is_not: { other: null },
    integer: true,
    between: [0, 99],
  },
});
